import { useCallback, useEffect, useState } from 'react'
import { Button, EmptyState, Skeleton } from '../../../shared/ui'
import {
  fetchOutreachSettings,
  listCustomFieldDefs,
  listPrograms,
  listSequences,
  listSteps,
} from '../../../lib/outreach/api'
import type { CustomFieldDef, Program, Sequence, SequenceStep } from '../../../lib/outreach/types'
import { ErrorLine, errorMessage } from './shared'
import { SequenceEditor } from './SequenceEditor'

type Editing = Sequence | 'new' | null

export function SequencesTab({ readOnly }: { readOnly: boolean }) {
  const [sequences, setSequences] = useState<Sequence[]>([])
  const [steps, setSteps] = useState<Record<string, SequenceStep[]>>({})
  const [programs, setPrograms] = useState<Program[]>([])
  const [defs, setDefs] = useState<CustomFieldDef[]>([])
  const [businessPhrases, setBusinessPhrases] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [editing, setEditing] = useState<Editing>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [seqs, progs, fieldDefs, settings] = await Promise.all([
        listSequences(),
        listPrograms(),
        listCustomFieldDefs('contact'),
        fetchOutreachSettings(),
      ])
      const stepLists = await Promise.all(seqs.map((s) => listSteps(s.id)))
      setSequences(seqs)
      setSteps(Object.fromEntries(seqs.map((s, i) => [s.id, stepLists[i] ?? []])))
      setPrograms(progs)
      setDefs(fieldDefs)
      setBusinessPhrases(settings.blockedPhrases)
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  if (editing) {
    const seq = editing === 'new' ? null : editing
    const users = seq ? programs.filter((p) => p.sequence_id === seq.id) : []
    const phrases = [...businessPhrases, ...users.flatMap((p) => p.blocked_phrases)]
    const done = () => {
      setEditing(null)
      void load()
    }
    return (
      <SequenceEditor
        key={seq?.id ?? 'new'}
        sequence={seq}
        steps={seq ? (steps[seq.id] ?? []) : []}
        phrases={phrases}
        defs={defs}
        usedBy={users.length}
        readOnly={readOnly}
        onSaved={done}
        onDeleted={done}
        onCancel={() => setEditing(null)}
      />
    )
  }

  return (
    <div className="os-root">
      <div className="os-bar">
        <span className="settings-hint">
          A sequence is an ordered list of email steps that programs send.
        </span>
        {!readOnly && (
          <span className="os-bar-end">
            <Button onClick={() => setEditing('new')}>New sequence</Button>
          </span>
        )}
      </div>
      <ErrorLine>{error}</ErrorLine>
      {loading ? (
        <div className="os-card" aria-busy="true">
          <Skeleton width="40" />
        </div>
      ) : sequences.length === 0 ? (
        <EmptyState
          title="No sequences yet"
          message="A sequence holds the emails a program sends, with the days between them."
          {...(readOnly
            ? {}
            : { cta: { label: 'New sequence', onClick: () => setEditing('new') } })}
        />
      ) : (
        <div className="os-list">
          {sequences.map((s) => {
            const n = steps[s.id]?.length ?? 0
            return (
              <div className="os-card" key={s.id}>
                <div className="os-card-head">
                  <span className="os-name">{s.name}</span>
                  <span className="settings-hint">
                    {n} {n === 1 ? 'step' : 'steps'}
                  </span>
                  <span className="os-bar-end">
                    <Button variant="secondary" onClick={() => setEditing(s)}>
                      {readOnly ? 'View' : 'Edit'}
                    </Button>
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
